<script>
export default {
	name: 'ContinueYourSearch',
};
</script>

<script setup>
import dayjs from 'dayjs';
import VueSwiper from '~/components/VueSwiper.vue';
import usePersistencyWithExpiry from '~/logic/composables/usePersistencyWithExpiry.js';
import { isEqual as _isEqual } from 'lodash';
import { i18nGlobal } from '~/logic/i18n.js';
import { sleep } from '~/logic/helpers/utils';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { useONDList } from '~/logic/composables/booking-widget/useONDList.js';
import { useAuthStore } from '~/logic/auth/auth-store.js';

const props = defineProps({
	// modelValue: { type: String, default: '' },
	continueYourSearchLabel: { type: String, default: 'Continue your search' },
	economyLabel: { type: String, default: i18nGlobal.t('Economy') },
	businessLabel: { type: String, default: i18nGlobal.t('Business') },
	businessSuiteLabel: { type: String, default: i18nGlobal.t('Business Suite') },
	bstTagLabel: { type: String, default: 'Bonus Side Trip' },
	componentIdAA: { type: String, default: '' },
	enableGlassDesign: { type: Boolean, default: false },
	nonLoggedInMessage: { type: String, default: '' },
	activeSubTab: { type: String, default: '' },
});
const emit = defineEmits([
	'restore-past-search',
	'is-past-search-empty',
]);

const { pageProperties } = useGlobalAEMState();
const { optionsFrom	} = useONDList({ });

const {
	isLoggedIn,
} = useAuthStore();

const pastSearches = ref([]);

const siteName = window.siteName;

usePersistencyWithExpiry('flight-search:past-searches', {
	refs: {
		pastSearches,
	},
});

// Read airport code only; do not mutate the item
const getAirportCode = (ond) => {
	return String(ond?.airportCode ?? ond?.code ?? ond?.value ?? '')
		.trim()
		.toUpperCase();
};

const normDate = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : '');

// Signature uses OND + dates (+ one-way logic) + passenger counts
const getSearchSignature = (s) => {
	const from = getAirportCode(s?.ondValueFrom);
	const to = getAirportCode(s?.ondValueTo);

	const fromDate = normDate(s?.dateRangeValueFrom);
	const toDate = s?.isOneWay ? '' : normDate(s?.dateRangeValueTo);

	const adults = Number(s?.adultsCount ?? 0);
	const children = Number(s?.childrenCount ?? 0);
	const teens = Number(s?.teensCount ?? 0);
	const infants = Number(s?.infantsCount ?? 0);

	return [from, to, fromDate, toDate, adults, children, teens, infants].join('|');
};

/**
 * Save the latest search:
 * - Remove older duplicates (same OND + dates + pax)
 * - Prepend the latest
 * - Keep max 3 items
 */
const saveSearch = (data) => {
	const sig = getSearchSignature(data);

	// Filter out any previous entries with the same signature
	const deduped = pastSearches.value.filter(
		(item) => getSearchSignature(item) !== sig
	);

	// Prepend latest and cap at 3 (structure of items remains the same)
	pastSearches.value = [data, ...deduped].slice(0, 3);
};

const computedDisableTeleport = computed(() => {
	const teleportTarget = document.querySelector('#mobile-past-searches-container');
	if (teleportTarget) return false;
	return true;
});

const convertLocaleValue = (currentValue, code, langCode) => {
	let airportLocaleList = optionsFrom.value;
	if (pageProperties.value.rootLanguage === langCode) {
		return currentValue;
	} else {
		let hasLocale = airportLocaleList.find((item) => {
			return item.airportCode === code;
		})?._original;
		return hasLocale?.locale ? hasLocale.locale.cityName : hasLocale?.city;
	}
};

const getParsedDate = (item, index) => {
	const departure = dayjs(item.dateRangeValueFrom);
	if (item.isOneWay) {
		return departure.format('DD MMM YYYY');
	}
	const returnDate = dayjs(item.dateRangeValueTo);
	const isSameMonthYear = departure.year() === returnDate.year() && departure.month() === returnDate.month();
	if (index === 0) {
		return `${departure.format('DD MMM YYYY')} - ${returnDate.format('DD MMM YYYY')}`;
	}
	if (isSameMonthYear) {
		return `${departure.format('DD')} - ${returnDate.format('DD MMM YYYY')}`;
	}
	return `${departure.format('DD')} - ${returnDate.format('DD MMM YYYY')}`;
	
};

const getCabinClassLabelByValue = (val) => {
	switch (parseInt(val, 10)) {
		case 1: {
			return props.economyLabel;
		}
		case 2: {
			return props.businessLabel;
		}
		case 3: {
			return props.businessSuiteLabel;
		}
		default: {
			break;
		}
	}
};

const removeSearch = (searchItem) => {
	const sig = getSearchSignature(searchItem);
	const filteredList = pastSearches.value.filter(
		(item) => getSearchSignature(item) !== sig
	);
	pastSearches.value = filteredList;
	if (pastSearches.value?.length === 0) {
		emit('is-past-search-empty', { isEmpty: true });
	}
};

// 👇 This component got rendered before the initialization of loadDayjsLocale(dayjsLocale).
// 👇 Hence, the date month is not translated on page-load.
// 👇 This is re-evaluate and re-render the list value after loadDayjsLocale(dayjsLocale) initializes.
onMounted(async () => {
	await sleep(1000);
	const seen = new Set();
	const cleaned = [];
	for (const item of pastSearches.value) {
		const sig = getSearchSignature(item);
		if (!seen.has(sig)) {
			seen.add(sig);
			cleaned.push(item);
		}
	}
	pastSearches.value = cleaned.slice(0, 3);
});

defineExpose({
	saveSearch,
});

const isChineseDateFormat = ref(false);
const urlValues = ['zh_tw', 'zh_CN', 'zh_hk'];
onMounted(() => {
	urlValues.forEach((lang) => {
		if (window.location.href.includes(lang)) {
			isChineseDateFormat.value = true;
		}
	});
});

</script>

<template>
<div
	v-if="pastSearches && pastSearches.length > 0"
	class="ContinueYourSearch overflow-hidden"
>

	<div class="md:hidden">
		<p v-if="!props?.enableGlassDesign" class="typo-category-title mb-4 md:px-4">{{ props.continueYourSearchLabel }}</p>
		<div v-if="!props?.enableGlassDesign" class="continue-your-search-wrapper gap-6 lg:gap-3 w-full">
			<button
				v-for="(item, index) in pastSearches"
				:key="index"
				v-aa="[
					{
						clickName: `From: ${item.ondValueFrom.displayValue ?? '-'} | To : ${item.ondValueTo.displayValue ?? '-'} | Departure date: ${dayjs(item.dateRangeValueFrom).format('DD MMM YYYY') ?? '-'} | Return date: ${dayjs(item.dateRangeValueTo).format('DD MMM YYYY') ?? '-'} | BST: ${item.isBSTTab} | Passenger: ${ item.adultsCount + item.teensCount + item.childrenCount + item.infantsCount } | Cabin class: ${item.cabinClassValue.label}`,
						clickComponentType: 'Button',
						componentName: 'ContinueYourSearch',
						componentID: props.componentIdAA,
					},
					{
						name: `From: ${item.ondValueFrom.displayValue ?? '-'} | To : ${item.ondValueTo.displayValue ?? '-'} | Departure date: ${dayjs(item.dateRangeValueFrom).format('DD MMM YYYY') ?? '-'} | Return date: ${dayjs(item.dateRangeValueTo).format('DD MMM YYYY') ?? '-'} | BST: ${item.isBSTTab} | Passenger: ${ item.adultsCount + item.teensCount + item.childrenCount + item.infantsCount } | Cabin class: ${item.cabinClassValue.label}`,
						type: 'other',
					},
				]"
				type="button"
				class="continue-your-search-tile"
				@click="$emit('restore-past-search', item)"
			>
				<icon-far-magnifying-glass class="icon fill-neutral-grey-dark mr-3 rtl:(mr-0 ml-3) mt-0.5" />
				
				<div class="flex flex-col">
					<div class="flex items-center flex-wrap">
						<span class="font-semibold">{{ convertLocaleValue(item.ondValueFrom.displayValue, item.ondValueFrom.airportCode, item.locale) }} </span>
						<icon-fas-plane class="fill-neutral-grey-dark mx-3 transform rtl:-rotate-180" />
						<span class="font-semibold">{{ convertLocaleValue(item.ondValueTo.displayValue, item.ondValueTo.airportCode, item.locale) }} </span>
					</div>
					<time class="mt-2">
						<template v-if="item.dateRangeValueTo && dayjs(item.dateRangeValueTo).isValid()">
							{{ isChineseDateFormat ? $t('{dateFrom} to {dateTo}', {
								dateFrom: dayjs(item.dateRangeValueFrom).format('YYYY MMM DD'),
								dateTo: dayjs(item.dateRangeValueTo).format('YYYY MMM DD'),
							}) : $t('{dateFrom} to {dateTo}', {
								dateFrom: dayjs(item.dateRangeValueFrom).format('DD MMM YYYY'),
								dateTo: dayjs(item.dateRangeValueTo).format('DD MMM YYYY'),
							}) }}
						</template>
						<template v-else>
							<span>{{ isChineseDateFormat ? dayjs(item.dateRangeValueFrom).format('YYYY MMM DD') : dayjs(item.dateRangeValueFrom).format('DD MMM YYYY') }}</span>
						</template>
					</time>

					<div v-if="item.isBSTTab" class="mt-2">
						<div class="tag-label">{{ $t(props.bstTagLabel) }}</div>
					</div>

					<div v-else-if="item.payByEnrichPoints" class="mt-2">
						<div v-if="item.activeSubOption" class="tag-label">{{ item.activeSubOption }}</div>
					</div>

					<div v-else-if="item.isExpressBooking" class="mt-2">
						<div v-if="item.expressBookingLabel" class="tag-label">{{ item.expressBookingLabel }}</div>
					</div>

					<div class="mt-auto pt-3 flex items-center">
						<span class="inline-flex items-center mr-4 rtl:mr-0 rtl:ml-4">
							{{ item.adultsCount + item.teensCount + item.childrenCount + item.infantsCount }}
							<icon-fas-user class="fill-neutral-grey-ultradark w-2.5 mx-2" />
						</span>
						<span>{{ getCabinClassLabelByValue(item.cabinClassValue.value) }}</span>
					</div>
				</div>
			</button>
		</div>
		<div
			v-else
			class="rafi-glass-design-wrapper gap-2 flex w-full"
		>
			<button
				v-for="(item, index) in pastSearches"
				:key="index"
				v-aa="[
					{
						clickName: `From: ${item.ondValueFrom.displayValue ?? '-'} | To : ${item.ondValueTo.displayValue ?? '-'} | Departure date: ${dayjs(item.dateRangeValueFrom).format('DD MMM YYYY') ?? '-'} | Return date: ${dayjs(item.dateRangeValueTo).format('DD MMM YYYY') ?? '-'} | BST: ${item.isBSTTab} | Passenger: ${ item.adultsCount + item.teensCount + item.childrenCount + item.infantsCount } | Cabin class: ${item.cabinClassValue.label}`,
						clickComponentType: 'Button',
						componentName: 'ContinueYourSearch',
						componentID: props.componentIdAA,
					},
					{
						name: `From: ${item.ondValueFrom.displayValue ?? '-'} | To : ${item.ondValueTo.displayValue ?? '-'} | Departure date: ${dayjs(item.dateRangeValueFrom).format('DD MMM YYYY') ?? '-'} | Return date: ${dayjs(item.dateRangeValueTo).format('DD MMM YYYY') ?? '-'} | BST: ${item.isBSTTab} | Passenger: ${ item.adultsCount + item.teensCount + item.childrenCount + item.infantsCount } | Cabin class: ${item.cabinClassValue.label}`,
						type: 'other',
					},
				]"
				type="button"
				class="glass-design-tile"
				@click="$emit('restore-past-search', item)"
			>
				<div class="flex flex-row items-center px-2">
					<span class="font-semibold airport-code-text">{{ item.ondValueFrom.airportCode }} - {{ item.ondValueTo.airportCode }}</span>
					<span class="font-light date-text pl-1">
						{{ getParsedDate(item, index) }}
					</span>
					<icon-fas-xmark class="w-5 pl-2.5 pt-0.5" aria-hidden="true" @click.stop="() => removeSearch(item)" />
				</div>
			</button>
		</div>
	</div>

	<div class=">md:hidden -mx-4">
		<Teleport v-if="!props?.enableGlassDesign" to="#mobile-past-searches-container" :disabled="computedDisableTeleport">
			<p class="typo-category-title mb-4 md:px-4 pt-8">{{ props.continueYourSearchLabel }}</p>
			<VueSwiper
				class="mobile-swiper"
				slidesPerView="auto"
				:spaceBetween="16"
				variant="slides-auto-width slides-auto-height"
				:scrollbar="{
					draggable: true,
					hide: true,
				}"
				style="--tile-max-width: 260px;"
				:data-use-theme="siteName"
			>
				<button
					v-for="(item, index) in pastSearches"
					:key="index"
					v-aa="[
						{
							clickName: `From: ${item.ondValueFrom.displayValue ?? '-'} | To : ${item.ondValueTo.displayValue ?? '-'} | Departure date: ${dayjs(item.dateRangeValueFrom).format('DD MMM YYYY') ?? '-'} | Return date: ${dayjs(item.dateRangeValueTo).format('DD MMM YYYY') ?? '-'} | BST: ${item.isBSTTab} | Passenger: ${ item.adultsCount + item.teensCount + item.childrenCount + item.infantsCount } | Cabin class: ${item.cabinClassValue.label}`,
							clickComponentType: 'Button',
							componentName: 'ContinueYourSearch',
							componentID: props.componentIdAA,
						},
						{
							name: `From: ${item.ondValueFrom.displayValue ?? '-'} | To : ${item.ondValueTo.displayValue ?? '-'} | Departure date: ${dayjs(item.dateRangeValueFrom).format('DD MMM YYYY') ?? '-'} | Return date: ${dayjs(item.dateRangeValueTo).format('DD MMM YYYY') ?? '-'} | BST: ${item.isBSTTab} | Passenger: ${ item.adultsCount + item.teensCount + item.childrenCount + item.infantsCount } | Cabin class: ${item.cabinClassValue.label}`,
							type: 'other',
						},
					]"
					type="button"
					class="continue-your-search-tile"
					@click="$emit('restore-past-search', item)"
				>
					<icon-far-magnifying-glass class="icon fill-neutral-grey-dark mr-3 rtl:(mr-0 ml-3) mt-0.5" />
					
					<div class="flex flex-col">
						<div class="flex items-center flex-wrap">
							<span class="font-semibold">{{ convertLocaleValue(item.ondValueFrom.displayValue, item.ondValueFrom.airportCode, item.locale) }}</span>
							<icon-fas-plane class="fill-neutral-grey-dark mx-3 transform rtl:-rotate-180" />
							<span class="font-semibold">{{ convertLocaleValue(item.ondValueTo.displayValue, item.ondValueTo.airportCode, item.locale) }}</span>
						</div>
						<time class="mt-2">
							<template v-if="item.dateRangeValueTo && dayjs(item.dateRangeValueTo).isValid()">
								{{ $t('{dateFrom} to {dateTo}', {
									dateFrom: dayjs(item.dateRangeValueFrom).format('DD MMM YYYY'),
									dateTo: dayjs(item.dateRangeValueTo).format('DD MMM YYYY'),
								}) }}
							</template>
							<template v-else>
								<span>{{ dayjs(item.dateRangeValueFrom).format('DD MMM YYYY') }}</span>
							</template>
						</time>
						
						<div v-if="item.isBSTTab" class="mt-2">
							<div class="tag-label">{{ $t(props.bstTagLabel) }}</div>
						</div>
						
						<div v-else-if="item.isExpressBooking" class="mt-2">
							<div v-if="item.expressBookingLabel" class="tag-label">{{ item.expressBookingLabel }}</div>
						</div>
						
						<div class="mt-auto pt-3 flex items-center">
							<span class="inline-flex items-center mr-4 rtl:mr-0 rtl:ml-4">
								{{ item.adultsCount + item.teensCount + item.childrenCount + item.infantsCount }}
								<icon-fas-user class="fill-neutral-grey-ultradark w-2.5 mx-2" />
							</span>
							<span class="">{{ item.cabinClassValue.label }}</span>
							<span v-if="item.payByEnrichPoints">
								<span v-if="item.activeSubOption" class="tag-label">{{ item.activeSubOption }}</span>
							</span>
						</div>
					</div>
				</button>
			</VueSwiper>
		</Teleport>
		<VueSwiper
			v-if="props?.enableGlassDesign"
			class="mobile-swiper glass-design"
			slidesPerView="auto"
			:spaceBetween="16"
			variant="slides-auto-width slides-auto-height"
			:scrollbar="{
				draggable: true,
				hide: true,
			}"
			style="--tile-max-width: 260px;"
			:data-use-theme="siteName"
		>
			<button
				v-for="(item, index) in pastSearches"
				:key="index"
				v-aa="[
					{
						clickName: `From: ${item.ondValueFrom.displayValue ?? '-'} | To : ${item.ondValueTo.displayValue ?? '-'} | Departure date: ${dayjs(item.dateRangeValueFrom).format('DD MMM YYYY') ?? '-'} | Return date: ${dayjs(item.dateRangeValueTo).format('DD MMM YYYY') ?? '-'} | BST: ${item.isBSTTab} | Passenger: ${ item.adultsCount + item.teensCount + item.childrenCount + item.infantsCount } | Cabin class: ${item.cabinClassValue.label}`,
						clickComponentType: 'Button',
						componentName: 'ContinueYourSearch',
						componentID: props.componentIdAA,
					},
					{
						name: `From: ${item.ondValueFrom.displayValue ?? '-'} | To : ${item.ondValueTo.displayValue ?? '-'} | Departure date: ${dayjs(item.dateRangeValueFrom).format('DD MMM YYYY') ?? '-'} | Return date: ${dayjs(item.dateRangeValueTo).format('DD MMM YYYY') ?? '-'} | BST: ${item.isBSTTab} | Passenger: ${ item.adultsCount + item.teensCount + item.childrenCount + item.infantsCount } | Cabin class: ${item.cabinClassValue.label}`,
						type: 'other',
					},
				]"
				type="button"
				class="glass-design-tile"
				@click="$emit('restore-past-search', item)"
			>
				<div class="flex flex-row items-center px-2">
					<span class="font-semibold text-sm">{{ item.ondValueFrom.airportCode }} - {{ item.ondValueTo.airportCode }}</span>
					<span class="font-light text-xs pl-1">
						{{ getParsedDate(item, index) }}
					</span>
					<icon-fas-xmark class="w-5 pl-2.5" aria-hidden="true" @click.stop="() => removeSearch(item)" />
				</div>
			</button>
		</VueSwiper>
	</div>
	
</div>
<div v-else-if="props.enableGlassDesign && pastSearches.length == 0 && props.activeSubTab !== 'redeem-flight' && props.activeSubTab !== 'multicity'">
	<div v-if="!isLoggedIn" class="w-full">
		<div class="login-disclaimer flex justify-between text-xs w-full">
			<span v-html-sanitize="props.nonLoggedInMessage"></span>
		</div>
	</div>
</div>


</template>


<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.ContinueYourSearch {
	
}

.continue-your-search-wrapper {
	--tile-max-width: 320px;
	display: grid;
	grid-auto-columns: minmax(0, var(--tile-max-width));
	grid-auto-flow: column;
}

.continue-your-search-tile {
	border-radius: 12px;
	border: 1.5px solid var(--neutral-grey-light);
	background-color: var(--neutral-grey-ultralight);
	color: var(--neutral-grey-ultradark);
	display: flex;
	align-items: stretch;
	text-align: left;
	flex-basis: var(--tile-max-width);
	min-height: 140px;
	transition: border-color 250ms, background-color 250ms;
	@apply p-4 text-sm;

	html[dir="rtl"] & {
		text-align: right;
	}
	
	&:hover {
		border-color: var(--neutral-grey-dark);
		background-color: var(--neutral-grey-extralight);
	}
	.icon {
		flex: 0 0 16px;
	}
	.tag-label {
		text-transform: uppercase;
		border-radius: 4px;
		color: white;
		display: inline-flex;
		background-color: var(--primary-blue-base);
		font-weight: 600;
		@apply px-3 py-1 text-xs;
	}
	
	@media #{var.$query-max-md} {
		height: 100%;
		width: var(--tile-max-width);
	}
}

.glass-design-tile {
	width: auto;
	height: 40px;
	opacity: 1;
	border-radius: 100px;
	border-width: 1px;
	padding-top: 8px;
	padding-bottom: 8px;
	background-color: #EFF9FF;
	border: 1px solid #C7EAFB;
	color: #000;

	:deep(.airport-code-text) {
		display: inline-block;
		font-size: 14px;
	}

	:deep(.date-text) {
		display: inline-block;
		font-size: 12px;
	}

	:deep(svg) {
		width: 20px !important;
		height: 20px !important;
	}
}

.mobile-swiper {
	@apply pb-6;
	
	:deep(.swiper-slide:first-child) {
		@apply pl-4;
		
		html[dir="rtl"] & {
			@apply pl-0 pr-4;
		}
	}
	:deep(.swiper-slide:last-child) {
		@apply pr-4;
		
		html[dir="rtl"] & {
			@apply pr-0 pl-4;
		}
	}
	:deep(.swiper-scrollbar) {
		width: 80px;
		margin-left: auto;
		margin-right: auto;
		left: 0;
		right: 0;
		
		.swiper-scrollbar-drag {
			background-color: var(--primary-blue-base);
		}
	}
}

.mobile-swiper {

	&.glass-design {
		padding-bottom: 0 !important;
	}
}

[data-use-theme="amal"] {
	
	&.mobile-swiper {
		
		:deep(.swiper-scrollbar) {
			
			.swiper-scrollbar-drag {
				background-color: var(--secondary-amal-red-base);
			}
		}
	}
}

</style>
